import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../config";

const TZ = {
  CST: { key: "CST", label: "CST", iana: "America/Chicago" },
  IST: { key: "IST", label: "IST", iana: "Asia/Kolkata" },
};

const Sessions = () => {
  const [sessions, setSessions] = useState([]);
  const [hosts, setHosts] = useState({});
  const [analysts, setAnalysts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [tzKey, setTzKey] = useState("CST");
  const activeTZ = TZ[tzKey];
  const navigate = useNavigate();

  const timeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat("en-US", {
        timeZone: activeTZ.iana,
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    [activeTZ.iana]
  );

  const dateShortFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat("en-US", {
        timeZone: activeTZ.iana,
        month: "short",
        day: "numeric",
      }),
    [activeTZ.iana]
  );

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("sessions")
        .select("*")
        .order("session_id", { ascending: true });
      if (error) throw error;
      setSessions(data || []);

      // Fetch host and analyst details
      const hostIds = [...new Set((data || []).map((s) => s.host_id).filter(Boolean))];
      const analystIds = [...new Set((data || []).map((s) => s.analyst_id).filter(Boolean))];
      const allIds = [...new Set([...hostIds, ...analystIds])];

      if (allIds.length > 0) {
        const { data: people, error: pErr } = await supabase
          .from("storytellers")
          .select("storyteller_id, storyteller_telugu, storyteller_english, photo_url")
          .in("storyteller_id", allIds);
        if (!pErr && people) {
          const hMap = {};
          const aMap = {};
          people.forEach((p) => {
            hMap[p.storyteller_id] = p;
            aMap[p.storyteller_id] = p;
          });
          setHosts(hMap);
          setAnalysts(aMap);
        }
      }
    } catch (err) {
      console.error("Error fetching sessions:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (utcStr) => timeFormatter.format(new Date(utcStr));
  const formatDate = (utcStr) => dateShortFormatter.format(new Date(utcStr));

  const day1Sessions = sessions.filter((s) => s.day === 1);
  const day2Sessions = sessions.filter((s) => s.day === 2);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-gray-600 animate-pulse">Loading sessions...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 px-4 py-8 flex flex-col items-center">
      {/* Header */}
      <img
        src={process.env.PUBLIC_URL + "/kathakala_utsavam_cover.png"}
        alt="Kathakala Utsavam Cover"
        className="w-full max-w-5xl rounded-lg shadow-md mb-4 object-cover"
        style={{ aspectRatio: "820 / 312" }}
      />
      <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2 text-center">
        కథాకళ ఉత్సవం సభలు — Kathakala Utsavam Sessions
      </h1>
      <p className="text-gray-600 mb-4 text-center">
        13 sessions across 2 days of storytelling
      </p>

      {/* Controls */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex rounded-md overflow-hidden border border-gray-300">
          {["CST", "IST"].map((tz) => (
            <button
              key={tz}
              onClick={() => setTzKey(tz)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                tzKey === tz
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              }`}
            >
              {tz}
            </button>
          ))}
        </div>
      </div>

      {/* Day 1 */}
      <DaySection
        title="Day 1 — USA/Canada Start — March 21"
        sessions={day1Sessions}
        hosts={hosts}
        analysts={analysts}
        formatTime={formatTime}
        formatDate={formatDate}
        activeTZ={activeTZ}
        navigate={navigate}
      />

      {/* Day 2 */}
      <DaySection
        title="Day 2 — Bharat Start — March 22"
        sessions={day2Sessions}
        hosts={hosts}
        analysts={analysts}
        formatTime={formatTime}
        formatDate={formatDate}
        activeTZ={activeTZ}
        navigate={navigate}
      />
    </div>
  );
};

/* ─── Day Section with Carousel + Grid ─── */
const DaySection = ({ title, sessions, hosts, analysts, formatTime, formatDate, activeTZ, navigate }) => {
  const scrollRef = useRef(null);
  const [viewMode, setViewMode] = useState("grid"); // "carousel" | "grid"

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    const amount = 340;
    scrollRef.current.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  };

  return (
    <div className="w-full max-w-6xl mb-10">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800">{title}</h2>
        <div className="flex rounded-md overflow-hidden border border-gray-300">
          <button
            onClick={() => setViewMode("grid")}
            className={`px-3 py-1 text-xs font-medium ${
              viewMode === "grid" ? "bg-blue-600 text-white" : "bg-white text-gray-700"
            }`}
          >
            Grid
          </button>
          <button
            onClick={() => setViewMode("carousel")}
            className={`px-3 py-1 text-xs font-medium ${
              viewMode === "carousel" ? "bg-blue-600 text-white" : "bg-white text-gray-700"
            }`}
          >
            Carousel
          </button>
        </div>
      </div>

      {viewMode === "carousel" ? (
        <div className="relative">
          {/* Left arrow */}
          <button
            onClick={() => scroll("left")}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 shadow-md rounded-full w-10 h-10 flex items-center justify-center text-gray-700 hover:bg-gray-100 -ml-2"
          >
            ◀
          </button>

          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory px-8 pb-4 hide-scrollbar"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {sessions.map((session) => (
              <SessionCard
                key={session.session_id}
                session={session}
                hosts={hosts}
                analysts={analysts}
                formatTime={formatTime}
                formatDate={formatDate}
                activeTZ={activeTZ}
                navigate={navigate}
                className="min-w-[300px] snap-start"
              />
            ))}
          </div>

          {/* Right arrow */}
          <button
            onClick={() => scroll("right")}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 shadow-md rounded-full w-10 h-10 flex items-center justify-center text-gray-700 hover:bg-gray-100 -mr-2"
          >
            ▶
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sessions.map((session) => (
            <SessionCard
              key={session.session_id}
              session={session}
              hosts={hosts}
              analysts={analysts}
              formatTime={formatTime}
              formatDate={formatDate}
              activeTZ={activeTZ}
              navigate={navigate}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Session Card ─── */
const SessionCard = ({ session, hosts, analysts, formatTime, formatDate, activeTZ, navigate, className = "" }) => {
  const host = hosts[session.host_id];
  const analyst = analysts[session.analyst_id];

  // Generate CST and IST times
  const cstFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Chicago",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  const istFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  const cstFrom = cstFormatter.format(new Date(session.session_from));
  const cstTo = cstFormatter.format(new Date(session.session_to));
  const istFrom = istFormatter.format(new Date(session.session_from));
  const istTo = istFormatter.format(new Date(session.session_to));

  const bgGradient = session.day === 1
    ? "from-orange-50 to-amber-50 border-orange-200"
    : "from-blue-50 to-indigo-50 border-blue-200";

  return (
    <div
      onClick={() => navigate(`/sessions/${session.session_title}`)}
      className={`bg-gradient-to-br ${bgGradient} border rounded-xl shadow-sm hover:shadow-lg transition-all cursor-pointer overflow-hidden ${className}`}
    >
      {/* Poster image placeholder */}
      {session.poster_image_url ? (
        <img
          src={session.poster_image_url}
          alt={session.session_honor_title_english}
          className="w-full aspect-video object-cover"
        />
      ) : (
        <div className="w-full aspect-video bg-gradient-to-br from-gray-700 to-gray-900 flex flex-col items-center justify-center text-white p-4">
          <span className="text-3xl font-bold mb-1">Session {session.session_id}</span>
          <span className="text-xl font-medium opacity-90">
            {session.session_honor_title_telugu}
          </span>
          <span className="text-sm opacity-70 mt-1">
            {session.session_honor_title_english}
          </span>
        </div>
      )}

      {/* Session info */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Session {session.session_id}
          </span>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            session.day === 1 ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"
          }`}>
            Day {session.day}
          </span>
        </div>

        <h3 className="text-lg font-bold text-gray-800 mb-1">
          {session.session_honor_title_telugu}
        </h3>
        <h4 className="text-sm font-medium text-gray-600 mb-3">
          {session.session_honor_title_english}
        </h4>

        {/* Times */}
        <div className="text-xs text-gray-600 space-y-0.5 mb-3">
          <div>🇺🇸 {cstFrom} – {cstTo} CST</div>
          <div>🇮🇳 {istFrom} – {istTo} IST</div>
        </div>

        {/* Host & Analyst */}
        <div className="flex gap-2 text-xs">
          {host && (
            <div className="flex items-center gap-1.5 bg-green-50 px-2 py-1 rounded-md">
              {host.photo_url && (
                <img src={host.photo_url} alt="" className="w-5 h-5 rounded-full object-cover" />
              )}
              <span className="text-green-800 font-medium truncate max-w-[100px]">
                🎤 {host.storyteller_english}
              </span>
            </div>
          )}
          {analyst && (
            <div className="flex items-center gap-1.5 bg-purple-50 px-2 py-1 rounded-md">
              {analyst.photo_url && (
                <img src={analyst.photo_url} alt="" className="w-5 h-5 rounded-full object-cover" />
              )}
              <span className="text-purple-800 font-medium truncate max-w-[100px]">
                📝 {analyst.storyteller_english}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sessions;
